// TODO: update to show completed user rank information

import React from "react";
import PropTypes from "prop-types";
import { Portal } from "react-portal";

import setModalAppElement from "lib/setModalAppElement";
import IconContext from "components/utils/IconContext";
import RewardConfetti from "./RewardConfetti";
import WebComponent from "../WebComponent";
import { t } from 'lib/I18n';

setModalAppElement();

export default class TrailCompletedModal extends React.Component {
  SERIES_CTA_EVENT_NAME = "cta_clicked";
  SERIES_MODAL_PAGE_LOCATION = "th-journey-celebration-modal";
  VIEW_SERIES_LABEL = "view-trailhead-journey";
  START_NEXT_TRAIL_LABEL = "start-next-trail-in-journey";

  static propTypes = {
    title: PropTypes.string.isRequired,
    sharing: PropTypes.object,
    celebrationHeader: PropTypes.string.isRequired,
    footerCta: PropTypes.string.isRequired,
    trailsLink: PropTypes.string.isRequired,
    shareCta: PropTypes.string.isRequired,
    isOpen: PropTypes.bool,
    inProgressTrails: PropTypes.array,
    preferences: PropTypes.object,
    trailSeriesCelebration: PropTypes.object,
    trailSeriesCompletionCelebration: PropTypes.object,
  };

  state = {
    isOpen: this.props.isOpen,
    showFooterLinks: true
  };

  modal = React.createRef();

  closeModal = () => {
    this.trackModalClose();
    this.setState({ isOpen: false });
    this.resetFocus();
  };

  componentDidMount() {
    this.setLastPageCtaProp();

    if (this.modal.current) {
      this.modal.current.addEventListener("requestclose", this.closeModal);
      this.modal.current.addEventListener("statechange", this.handleCollectionStateChange);
      window.addEventListener("lpstatechange", this.handleLpStateChange);
    }

    if (window.lpCelebrationState === "open") {
      this.setState({ isOpen: true });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isOpen && this.state.isOpen && window.emitter?.emit) {
      /**
       * Notes:
       * - Timeout exists to ensure that the th-streaks-manager's API call finishes before the event
       * is attempted. Otherwise, the emitter's event listener may have not been wired up yet.
       * For localhost testing, you might need to increase this from 500 to ~800ms.
       *
       * - Streaks celebrations should only happen on the unit page before this modal
       * is opened for Trail-complete celebrations. So the Streak-related emitter below
       * exists in case the user returns to this page at a later point in time, after their Streak
       * has been extended by completing the Trail.
       */
      setTimeout(() => {
        window.emitter.emit("custom", {
          name: "award_earned",
          payload: {},
        });
      }, 500);
    }
  }

  lastPageCtaConfig = () => {
    if (this.hasTrailSeriesCelebrationPage() || this.hasTrailSeriesCompletionCelebrationPage()) {
      return null;
    }

    if(this.props.inProgressTrails.length && this.props.trailsLink) {
      return {
        href: "/trails",
        label: this.props.trailsLink,
      };
    } else {
      return {
        href: "/today",
        label: t('reward.button.explore_more'),
        header: t('reward.encourage'),
        description: t('reward.button.explore_more_description')
      };
    }
  }

  setLastPageCtaProp = () => {
    const lastPageCta = this.lastPageCtaConfig();

    if (this.modal.current) {
      this.modal.current.lastPageCta = lastPageCta ? { ...lastPageCta } : null;
    }
  };

  handleLpStateChange = (event) => {
    if (event.detail.state === "open") {
      this.setState({ isOpen: true });
    }
  };

  handleCollectionStateChange = (event) => {
    if (event.detail.state === "empty") {
      // Ensure the last page is removed if th-content-collection (th-recommended-trails) returns an empty state after API call
      this.modal.current.removeLastPageSlot();
      event.stopPropagation();
    }
  };

  resetFocus = () => {
    document.querySelector(".th-content-container").focus();
  };

  // Note: Linkedin does not accept text but needs an identifier to toggle the button
  renderSharing = () => {
    return (
      <div>
        <th-social-sharing
          app-id={this.props.sharing.fbAppId}
          content-title={this.props.title}
          content-description={this.props.description}
          public={true}
          linkedin-text={"toggle"}
        />
      </div>
    );
  };

  renderConfetti = () => {
    if (!this.state.isOpen) {
      return null;
    }

    return (
      <Portal>
        <RewardConfetti colorScheme={this.props.colorScheme} />
      </Portal>
    );
  };

  renderFooterLinks = () => {
    if (this.props.inProgressTrails.length) {
      return (
        <div className="trail-completed-modal_footer-header" style={{ margin: "10px 0 20px 0" }}>
          <div>{this.props.footerCta}</div>
          <a href="/trails">{this.props.trailsLink}</a>
        </div>
      );
    }

    if (this.state.showFooterLinks) {
      return (
        <div>
          <div className="trail-completed-modal_footer-header" style={{ margin: "10px 0 5px 0" }}>
            <div>Guided Learning</div>
            <a href="/trails">{this.props.trailsLink}</a>
          </div>
          <div style={{ margin: "0 0 10px 0" }}>Discover trails to kickstart your learning journey.</div>
        </div>
      );
    }
  };

  renderTrails = () => {
    if (!this.props.inProgressTrails.length) {
      return this.renderRecommendedTrails();
    }

    return this.renderInProgressTrails();
  };

  renderRecommendedTrails = () => {
    return <WebComponent tagName="th-recommended-trails" attrs={{ preferences: this.props.preferences }} />;
  };

  renderInProgressTrails = () => (
    <ul>
      {this.props.inProgressTrails.map((trail) => (
        <li className="trail-completed-modal_trail-list-item" key={trail.slug}>
          <img className="trail-completed-modal_icon-image--small" src={this.props.trailheadLogoUrl} alt="" />
          <div className="info">
            <div className="type">Trail</div>
            <a href={this.inProgressTrailLink(trail)}>{trail.title}</a>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "15px" }}>
              <div className="points">+{trail.points}</div>

              <div style={{ display: "flex", alignItems: "center" }}>
                <span className="progress-label">{Math.floor(trail.progress)}%</span>
                <div className="progress-bar" style={{ width: "50px" }}>
                  <div className="progress-bar-inner" style={{ width: `${trail.progress || 0}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

  inProgressTrailLink = (trail) => {
    if (trail.audience === "employee") {
      return `/content/employee/trails/${trail.slug}`;
    }

    return `/content/learn/trails/${trail.slug}`;
  };

  hasTrailSeriesCelebrationPage = () => {
    return !!this.props.trailSeriesCelebration;
  };

  hasTrailSeriesCompletionCelebrationPage = () => {
    return !!this.props.trailSeriesCompletionCelebration;
  };

  isSeriesCelebrationModal = () => {
    return this.hasTrailSeriesCelebrationPage() || this.hasTrailSeriesCompletionCelebrationPage();
  };

  renderTrailSeriesCelebration = () => {
    const props = this.props.trailSeriesCelebration;
    if (!props) {
      return null;
    }
    const stats = props.seriesContentStats;
    const nextTrail = props.nextTrail;
    const trailLogoUrl = "https://res.cloudinary.com/trailhead/image/upload/public-trailhead/assets/images/trailhead-logo-white-outline.png";
    const nextTrailLevelLabel = nextTrail?.levelLabel || nextTrail?.level_label;
    const nextTrailType = nextTrail?.trailType || nextTrail?.trail_type;
    const nextTrailPointsLabel = nextTrail?.pointsLabel || nextTrail?.points_label;
    const nextTrailEstimatedTimeLabel = nextTrail?.estimatedTimeLabel || nextTrail?.estimated_time_label;
    const seriesContentUid = props.contentUid || props.content_uid;
    const nextTrailContentUid = nextTrail?.contentUid || nextTrail?.content_uid;

    return (
      <div slot="pageLast" className="th-modal__content--full-screen">
        <div className="trail-series-celebration-modal">
          <h1 className="th-celebration-modal__heading trail-series-celebration-modal__title">
            {props.title}
          </h1>

          {stats && (
            <div className="th-content-stats th-content-stats--series">
              <div className="th-content-stats_section">
                <span>{stats.pointsLabel}</span>
              </div>
              <div className="th-content-stats_divider"></div>
              <div className="th-content-stats_section">
                <span>{stats.trailsLabel}</span>
              </div>
              <div className="th-content-stats_divider"></div>
              <div className="th-content-stats_section">
                <span>{stats.completed ? (stats.finishedAt || "Completed") : `${stats.completedPercentage}%`}</span>
              </div>
            </div>
          )}

          <p className="trail-series-celebration-modal__congrats">
            {props.congratulationMessage}
          </p>

          {nextTrail && (
            <>
              <p className="trail-series-celebration-modal__next-prompt">
                {props.nextPrompt}
              </p>

              <div className="next-trail trail-series-trails__item trail-series-celebration-modal__next-trail">
                <div className="next-trail__container">
                  {nextTrailLevelLabel && (
                    <div className="next-trail__level-badge">{nextTrailLevelLabel}</div>
                  )}
                  <div className="next-trail__logo">
                    <img src={trailLogoUrl} alt="" />
                  </div>
                  <div className="next-trail__info">
                    <div className="trail-type">{nextTrailType}</div>
                    <div className="next-trail__title">
                      <a href={nextTrail.url}>{nextTrail.title}</a>
                    </div>
                    {nextTrail.description && (
                      <p className="next-trail__description">{nextTrail.description}</p>
                    )}
                    <div className="next-trail__stats">
                      {nextTrailPointsLabel && (
                        <div className="next-trail__stats__points">{nextTrailPointsLabel}</div>
                      )}
                      {nextTrailEstimatedTimeLabel && (
                        <div className="next-trail__stats__total-time">{nextTrailEstimatedTimeLabel}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="trail-series-celebration-modal__ctas">
            <a
              className="trail-series-celebration-modal__cta trail-series-celebration-modal__cta--secondary"
              href={props.seriesUrl}
              onClick={this.handleViewSeriesClick(props.seriesUrl, seriesContentUid)}
            >
              {props.goToSeriesCta}
            </a>
            {nextTrail && (
              <a
                className="trail-series-celebration-modal__cta trail-series-celebration-modal__cta--primary"
                href={nextTrail.url}
                onClick={this.handleStartNextTrailClick(nextTrail.url, nextTrailContentUid)}
              >
                {props.startNextTrailCta}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  renderTrailSeriesCompletionCelebration = () => {
    const props = this.props.trailSeriesCompletionCelebration;
    if (!props) {
      return null;
    }

    return (
      <div slot="pageLast" className="th-modal__content--full-screen">
        <div className="trail-series-celebration-modal series-completion-celebration-modal">
          <h1 className="th-celebration-modal__heading trail-series-celebration-modal__title">
            {props.title}
          </h1>

          <p className="series-completion-celebration-modal__completion-message">
            {props.completionMessage}
          </p>

          <p className="series-completion-celebration-modal__description">
            {props.description}
          </p>

          <div className="series-completion-celebration-modal__social" style={{ "--th-social-share-icon-width": "40px" }}>
            <th-social-sharing
              share-url={props.shareUrl}
              app-id={props.fbAppId}
              public={true}
              server-text={true}
              facebook-text={props.shareText}
              x-text={props.shareText}
              linkedin-text={props.shareText}
              hidden-social-icons="threads,bluesky"
            />
          </div>

          <div className="series-completion-celebration-modal__cta">
            <a className="trail-series-celebration-modal__cta trail-series-celebration-modal__cta--primary" href={props.exploreMoreUrl}>
              {props.exploreMoreCta}
            </a>
          </div>
        </div>
      </div>
    );
  };

  render() {
    return (
      <IconContext>
        <div className="trail-completed-modal_outer">
          <th-celebration-modal
            ref={this.modal}
            open={this.state.isOpen}
            assistive-text="trail completed"
          >
            <div slot="page1" className="th-modal__content--full-screen">
              <div className="trail-completed-modal">
                <h1 className="th-celebration-modal__heading">
                  {this.props.celebrationHeader}
                </h1>
                <div>
                  <img
                    className="trail-completed-modal_icon-image"
                    src={this.props.trailheadLogoUrl}
                    alt="trail completed"
                  />
                </div>
                <div className="trail-completed-modal_title">{this.props.title}</div>

                <div>{this.renderSharing()}</div>
              </div>
            </div>

            {this.hasTrailSeriesCompletionCelebrationPage() ? (
              this.renderTrailSeriesCompletionCelebration()
            ) : this.hasTrailSeriesCelebrationPage() ? (
              this.renderTrailSeriesCelebration()
            ) : (
              <div slot="pageLast" className="th-modal__content--full-screen">
                <div className="trail-completed-modal">
                  <div className="slds-p-around_medium trail-completed-modal_trails-list-page">
                    <h1 className="th-celebration-modal__heading">
                      {this.props.trailsListPageHeader}
                    </h1>
                    <p>
                      {this.props.trailsListPageDesc}
                    </p>
                    <div className="trail-completed-modal_trails-list-container">
                      {this.renderTrails()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </th-celebration-modal>
        </div>
        {this.renderConfetti()}
      </IconContext>
    );
  }

  dispatchTrackEvent = (eventName, payload) => {
    const trackEvent = new CustomEvent("trailhead_track", {
      bubbles: true,
      composed: true,
      detail: {
        eventName,
        payload,
      },
    });
    document.dispatchEvent(trackEvent);
  };

  trackModalClose = () => {
    if (!this.isSeriesCelebrationModal()) {
      return;
    }

    this.dispatchTrackEvent("modal_dismissed", {
      page_location: "th-journey-celebration-modal",
    });
  };

  handleStartNextTrailClick = (ctaHref, contentUid) => () => {
    this.dispatchTrackEvent(this.SERIES_CTA_EVENT_NAME, {
      label: this.START_NEXT_TRAIL_LABEL,
      cta_type: "primary",
      cta_href: ctaHref,
      page_location: this.SERIES_MODAL_PAGE_LOCATION,
      content_uid: contentUid,
    });
  };

  handleViewSeriesClick = (ctaHref, contentUid) => () => {
    this.dispatchTrackEvent(this.SERIES_CTA_EVENT_NAME, {
      label: this.VIEW_SERIES_LABEL,
      cta_type: "secondary",
      cta_href: ctaHref,
      page_location: this.SERIES_MODAL_PAGE_LOCATION,
      content_uid: contentUid,
    });
  };
}
